import { Flex, HStack, IconButton, Input, Stack, Status, Text, Link as ChakraLink } from '@chakra-ui/react'
import type { FlexProps } from '@chakra-ui/react'
import type { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { toaster } from '@app/components/ui/toaster'
import { Tooltip } from '@app/components/ui/tooltip'
import InlineUpdaterRoot from '@app/next/forms/inlineUpdaterRoot'
import AiDashboardReportRunNow from '@app/pages/aiDashboard/components/aiDashboardReportRunNow'
import type { DashboardItem } from '@app/pages/aiDashboard/types'
import Can from '@app/shared/authorization/can'
import ConfirmPopover from '@app/shared/confirmPopover'
import ICON_MAP from '@app/utils/iconMap'
import { useRecurringReportConfigurationDeleteMutation } from '@graphql/queries'
import type { Schedule } from '@graphql/types'

interface Props extends FlexProps {
  recurringReportConfiguration: DashboardItem
  schedule: Pick<Schedule, 'humanized'> | undefined
}

type PromptProps = {
  aiPrompt: string
}

const Prompt: FC<PromptProps> = ({ aiPrompt }) => {
  const txt = 'Prompt placeholder, please edit me before running.'

  // re: aiPrompt !== txt: handles older records that weren't allowed to have a nil aiPrompt
  if (aiPrompt && aiPrompt !== txt) {
    return (
      <Text as="pre" color="fg.emphasized" lineClamp={4} title={aiPrompt} fontSize="xs">
        {aiPrompt}
      </Text>
    )
  }

  return (
    <Text as="pre" color="fg.warning" lineClamp={4} title={txt} fontSize="xl">
      {txt}
    </Text>
  )
}

const AiDashboardReportHeader: FC<Props> = ({ recurringReportConfiguration, schedule, ...rest }) => {
  const navigate = useNavigate()
  const [{ fetching }, deleteConfiguration] = useRecurringReportConfigurationDeleteMutation()

  if (!recurringReportConfiguration) {
    return null
  }

  const { id, name, aiPrompt, sendToEmail, sendToSlack, strategyId } = recurringReportConfiguration

  const handleDelete = async () => {
    const response = await deleteConfiguration({
      input: {
        recurringReportConfigurationId: id
      }
    })

    if (!response.error) {
      toaster.create({
        title: 'Report configuration deleted',
        type: 'success',
        duration: 3000
      })

      navigate('..')
    } else {
      toaster.create({
        title: 'Error deleting report configuration',
        type: 'error',
        description: response.error.message,
        duration: 5000
      })
    }
  }

  return (
    <Flex justify="space-between" align="flex-start" gap="8" direction={{ base: 'column', md: 'row' }} {...rest}>
      <Stack gap="3">
        <HStack>
          <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
            <Input value={name || ''} name="name" placeholder="Report name..." fontSize="24px" border="none" p={0} />
          </InlineUpdaterRoot>
        </HStack>

        <HStack fontFamily="mono" color="fg.muted" textStyle="sm" gap="5" mb="2">
          {schedule?.humanized || ''}
          <Tooltip content={sendToEmail ? 'Email is enabled' : 'Email is disabled'} showArrow>
            <Status.Root colorPalette={sendToEmail ? 'green' : 'red'}>
              <Status.Indicator />
              Email
            </Status.Root>
          </Tooltip>

          <Tooltip content={sendToSlack ? 'Slack is enabled' : 'Slack is disabled'} showArrow>
            <Status.Root colorPalette={sendToSlack ? 'green' : 'red'}>
              <Status.Indicator />
              Slack
            </Status.Root>
          </Tooltip>
        </HStack>

        <ChakraLink asChild>
          <Link to={`/strategy/${strategyId}/dashboard/${id}/playground`}>
            <Prompt aiPrompt={aiPrompt} />
          </Link>
        </ChakraLink>
      </Stack>
      <Can I="update" an="strategy">
        <HStack gap="4">
          <AiDashboardReportRunNow recurringReportConfigurationId={id} />
          <ConfirmPopover
            header="Delete Report Configuration"
            body="Are you sure you want to delete this report configuration? This action cannot be undone."
            confirmButtonText="Delete"
            loading={fetching}
            onConfirm={handleDelete}
          >
            <IconButton aria-label="Delete report configuration" colorPalette="red" loading={fetching}>
              <ICON_MAP.Delete />
            </IconButton>
          </ConfirmPopover>
        </HStack>
      </Can>
    </Flex>
  )
}

export default AiDashboardReportHeader
